import { CheckSquare, TextSearch } from "lucide-react";
import * as React from "react";
import { useState } from "react";
import { graphql, useFragment } from "react-relay";
import { Markdown } from "src/components/ui/Markdown";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger
} from "src/components/ui/sheet";

import { SubTaskResults_lastToolCall$key } from "./__generated__/SubTaskResults_lastToolCall.graphql";
import { ToolCallButton } from "./ToolCallButton";
import { ToolCallSheet } from "./ToolCallSheet";

const ResultButton = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  function ResultButton({ children, ...props }, ref) {
    return (
      <div
        ref={ref}
        className="flex items-center gap-2 bg-secondary hover:bg-card text-foreground text-xs rounded px-2 py-1 border border-border text-left cursor-pointer w-fit"
        {...props}
      >
        <TextSearch className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="font-semibold">{children}</span>
      </div>
    );
  }
);

interface SubTaskResultsProps {
  lastToolCall: SubTaskResults_lastToolCall$key | null;
  toolContent: string;
  title: string;
  sources: any[];
}

const fragment = graphql`
  fragment SubTaskResults_lastToolCall on GraphQLMessageToolCall {
    ...ToolCallButton_toolCall
    ...ToolCallSheet_toolCall
  }
`;

export const SubTaskResults = React.memo<SubTaskResultsProps>(function SubTaskResults({
  lastToolCall: lastToolCallKey,
  toolContent,
  title,
  sources
}) {
  const lastToolCall = lastToolCallKey ? useFragment(fragment, lastToolCallKey) : null;
  const shouldShowLastToolCall = lastToolCall && !toolContent;
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const handleSheetOpen = () => {
    setIsSheetOpen(true);
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        {shouldShowLastToolCall && (
          <div className="flex flex-col gap-2">
            <ToolCallButton
              key="last-tool-call"
              toolCall={lastToolCall}
              onClick={handleSheetOpen}
            />
          </div>
        )}

        <div className="border border-border p-2 mt-2 rounded-md space-y-1">
          <Markdown>{
            (() => {
              // Hide sources in preview
              let processedContent = toolContent.replace(
                /`††call_[A-Za-z0-9_]+††`/g,
                "",
              );
              processedContent = processedContent.replace(
                /`††toolu_[A-Za-z0-9_]+††`/g,
                "",
              );
              processedContent = processedContent.replace(
                /`††tool_[A-Za-z0-9_]+††`/g,
                "",
              );
              // Remove artifact citations (V2 format without artifact_ prefix)
              processedContent = processedContent.replace(
                /`††[\w-]+\.json††`/g,
                "",
              );
              // Remove memory attribution annotations
              processedContent = processedContent.replace(
                /`?««memory:[a-f0-9-]+»»`?/gi,
                "",
              );
              const firstLine = processedContent.split("\n")[0];
              const firstChars = processedContent.slice(0, 300).trim();
              return firstLine.length < firstChars.length
                ? firstLine
                : firstChars + "...";
            })()
          }</Markdown>

          <Sheet>
            <SheetTrigger asChild>
              <ResultButton>See Details</ResultButton>
            </SheetTrigger>
            <SheetContent className="bg-popover text-foreground border-border w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl p-0 flex flex-col">
              <SheetTitle className="sr-only">Task Results</SheetTitle>
              <SheetDescription className="sr-only">Detailed results for the selected task</SheetDescription>

              <div className="flex justify-start items-center p-4 border-b border-border bg-secondary sticky top-0 z-10">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <CheckSquare className="h-5 w-5" />
                  {title}
                </h3>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <Markdown sources={sources}>{toolContent}</Markdown>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {isSheetOpen && lastToolCall && (
        <ToolCallSheet
          toolCall={lastToolCall}
          index={0}
          totalCount={1}
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          onNext={() => {}}
          onPrevious={() => {}}
        />
      )}
    </>
  );
});
